import type { ServerToClientEvent } from '@atlassian/jira-s2c-event/src/index.tsx';

let dispatcher: ClientDispatcher;

/**
 * The glue between a {@link ServerToClientEvent} and a client-side component.
 *
 * This class is responsible for ingesting events from the server and dispatching them to the correct component.
 *
 * Components hoping to be called from the server must be registered. Today, that's a manual call a developer
 * needs to make. Tomorrow, that may be automatically generated by the compiler reading a code annotation.
 */
class ClientDispatcher {
	#handlers: Map<string, Function>;

	#queuedPayloads: Map<string, ServerToClientEvent[]>;

	constructor() {
		this.#handlers = new Map();
		this.#queuedPayloads = new Map();
	}

	/**
	 * Ingest a payload from the server.
	 * This will trigger a callback to the component that registered for this event, otherwise the payload will be queued to do so later.
	 * @param payload - The payload to ingest.
	 */
	ingest(payload: ServerToClientEvent) {
		const handler = this.#handlers.get(payload.id);
		if (handler) {
			this.#exec(handler, payload);
		} else {
			const queue = this.#queuedPayloads.get(payload.id) || [];
			queue.push(payload);
			this.#queuedPayloads.set(payload.id, queue);
		}
	}

	/**
	 * Register a callback for a given event. If any payloads were queued for this event, they will be processed immediately.
	 * @param id - The event ID to register for.
	 * @param callback - The callback to trigger when the event is ingested.
	 */
	register(id: string, callback: Function) {
		this.#handlers.set(id, callback);

		// Process any queued payloads for this handler
		const queuedPayloads = this.#queuedPayloads.get(id) || [];
		if (queuedPayloads.length > 0) {
			queuedPayloads.forEach((payload) => this.#exec(callback, payload));
			this.#queuedPayloads.delete(id);
		}
	}

	/**
	 * Execute a callback with the given payload.
	 * @param callback - The callback to execute.
	 * @param payload - The payload to execute the callback with.
	 */
	#exec(callback: Function, payload: ServerToClientEvent) {
		const { args, error } = payload;
		try {
			if (error) {
				throw error;
			}
			callback(...args);
		} catch (err) {
			// TODO: log error
			// console.error(err);
		}
	}
}

const getClientDispatcher = () => {
	if (!dispatcher) {
		dispatcher = new ClientDispatcher();
	}
	return dispatcher;
};

export { getClientDispatcher, type ClientDispatcher };
